import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { getAdminMentorsApi, AdminMentorItem } from "../../features/analytics/api/adminAnalytics.api"
import { Avatar } from "../../shared/ui/Avatar"
import { HBarChart, GroupedBarChart, ChartCard } from "./Charts"
import { ChevronRightIcon, ExclamationTriangleIcon, UserGroupIcon } from "@heroicons/react/16/solid"

const LIMIT = 5

const Stars = ({ rating }: { rating: number }) => (
  <span className="inline-flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <span key={i} className={`text-[11px] ${i < Math.floor(rating) ? "text-amber-400" : "text-gray-200"}`}>★</span>
    ))}
    <span className="ml-1 text-xs text-gray-500">{rating}</span>
  </span>
)

export const MentorsAnalyticsPage = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<AdminMentorItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = () => {
    setLoading(true)
    setError(false)
    getAdminMentorsApi()
      .then((res) => setItems(res.data.items))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  if (error) {
    return (
      <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
        <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
        <p className="text-sm text-gray-400 mb-2">Не удалось загрузить менторов</p>
        <button onClick={load} className="text-xs font-medium text-blue-600 hover:text-blue-700 underline">
          Повторить
        </button>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {[1, 2].map((i) => <div key={i} className="bg-white rounded-2xl border border-gray-100 h-52 animate-pulse" />)}
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 h-64 animate-pulse" />
      </div>
    )
  }

  const totalPages = Math.max(1, Math.ceil(items.length / LIMIT))
  const pageItems = items.slice((page - 1) * LIMIT, page * LIMIT)

  return (
    <div className="space-y-5">
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartCard title="Выручка по менторам (сом)">
          <HBarChart
            data={items.map((m) => ({ label: m.full_name.split(" ")[0], value: m.total_revenue }))}
            color="#3b82f6"
          />
        </ChartCard>
        <ChartCard title="Просмотры курсов vs профиля">
          <GroupedBarChart
            data={items.map((m) => ({
              label: m.full_name.split(" ")[0],
              a: m.total_views,
              b: m.profile_views,
            }))}
            labelA="Курсы"
            labelB="Профиль"
            colorA="#3b82f6"
            colorB="#10b981"
          />
        </ChartCard>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b bg-gray-50/60 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Аналитика менторов</h3>
          <span className="text-xs text-gray-400">{items.length} менторов</span>
        </div>

        {items.length === 0 ? (
          <div className="px-5 py-16 text-center">
            <UserGroupIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Менторов пока нет</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/40">
                  {["Ментор", "Курсов", "Просм. курсов", "Покупки", "Конверсия", "Рейтинг", "Просм. профиля", "Выручка", ""].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {pageItems.map((m) => (
                  <tr key={m.mentor_id}
                    onClick={() => navigate(`/admin/analytics/mentors/${m.mentor_id}`)}
                    className="hover:bg-blue-50/40 transition-colors cursor-pointer group">
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <Avatar name={m.full_name} src={m.avatar_url} />
                        <span className="font-medium text-gray-800">{m.full_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-gray-600">{m.courses_count}</td>
                    <td className="px-4 py-3.5 text-gray-700 font-medium">{m.total_views.toLocaleString("ru")}</td>
                    <td className="px-4 py-3.5 text-gray-700">{m.total_purchases.toLocaleString("ru")}</td>
                    <td className="px-4 py-3.5">
                      <span className={`font-semibold ${m.avg_conversion_rate >= 7 ? "text-emerald-600" : m.avg_conversion_rate >= 5 ? "text-amber-600" : "text-red-500"}`}>
                        {m.avg_conversion_rate}%
                      </span>
                    </td>
                    <td className="px-4 py-3.5"><Stars rating={m.avg_rating} /></td>
                    <td className="px-4 py-3.5 text-gray-600">{m.profile_views.toLocaleString("ru")}</td>
                    <td className="px-4 py-3.5 text-gray-800 font-semibold whitespace-nowrap">
                      {(m.total_revenue / 1000).toFixed(0)}K сом
                    </td>
                    <td className="px-4 py-3.5">
                      <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-blue-500 transition-colors" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {items.length > 0 && (
          <div className="px-5 py-3.5 border-t bg-gray-50/40 flex items-center justify-between">
            <span className="text-xs text-gray-400">
              Стр. <span className="font-medium text-gray-600">{page}</span> из{" "}
              <span className="font-medium text-gray-600">{totalPages}</span>
            </span>
            <div className="flex gap-1.5">
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ← Пред.
              </button>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                След. →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
